import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  input,
  computed,
  signal,
  inject,
  ChangeDetectionStrategy,
  output,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil, combineLatest, debounceTime, distinctUntilChanged } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { EventsService } from 'app/modules/social-events/services/events.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeData from 'entity/BadgeData';
import BadgeQuestion from 'entity/BadgeQuestion';
import User from 'entity/User';
import UserRegistration from 'entity/UserRegistration';
import QuestionInputType from 'enums/QuestionInputType';
import { BadgeDataValue } from 'messages/websocket.messages';
import UserController from 'rest/UserController';

import { BadgingService } from '../../services/badging/badging.service';
import SocialEvent from 'entity/SocialEvent';

const userController = new UserController();

export const GUEST_EMAIL_KEY = 'guestEmail';

interface Question {
  featured: boolean;
  title: string;
  required: boolean;
  inputType: QuestionInputType;
  value: string;
  multiValues?: string[];
}

@Component({
  selector: 'app-badging-questions',
  templateUrl: './badging-questions.component.html',
  styleUrls: ['./badging-questions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BadgingQuestionsComponent extends Loggable implements OnInit, OnDestroy {
  protected badgingService = inject(BadgingService);
  private formBuilder = inject(UntypedFormBuilder);
  private store = inject(Store);
  private eventsService = inject(EventsService);

  public readonly socialRoomSetId = input('');
  public readonly event = input<SocialEvent>();
  public readonly joinSocial = output<boolean>();
  public readonly updatedSmartBadge = output<void>();

  public readonly isOnboardingExperience = input<boolean>(false);
  public readonly inRoom = input<boolean>(false);
  public readonly buttonText = computed(() =>
    this.inRoom() ? 'Update' : this.isOnboardingExperience() ? 'Continue' : 'Enter Scootaverse',
  );

  protected readonly showLoaderSignal = signal<boolean>(false);
  protected readonly showBadgePreviewSignal = signal<boolean>(false);

  protected readonly badgingFormSignal = signal<UntypedFormGroup | undefined>(undefined);

  protected readonly requiredBadgeQuestionsSignal = signal<BadgeQuestion[] | undefined>(undefined);
  protected readonly optionalBadgeQuestionsSignal = signal<BadgeQuestion[] | undefined>(undefined);
  protected readonly badgeQuestionsSignal = signal<Question[]>([]);
  protected readonly badgeDataValueSignal = signal<BadgeDataValue[]>([]);

  private readonly userSignal = signal<User | undefined>(undefined);
  private readonly isGuestSignal = signal<boolean>(false);
  private readonly firstNameSignal = signal<string | undefined>(undefined);
  private readonly lastNameSignal = signal<string | undefined>(undefined);

  protected readonly registrationSignal = signal<UserRegistration | undefined>(undefined);

  public showLoader?: boolean;
  public badgingForm?: UntypedFormGroup;
  protected requiredBadgeQuestions?: BadgeQuestion[];
  protected optionalBadgeQuestions?: BadgeQuestion[];
  protected badgeDataValue: BadgeDataValue[] = [];
  protected showBadgePreview = false;
  protected registration: UserRegistration | undefined;

  protected readonly hasRequiredErrorSignal = computed(() => {
    const form = this.badgingFormSignal();
    if (!form) {
      return false;
    }

    const controls = form.controls;
    for (const name in controls) {
      if (controls[name].hasError('required') && form.touched) {
        return true;
      }
    }
    return false;
  });

  // This regex is used to valid if a input has the title email
  private emailTitleRegex = '^[eE]+[mM]+[aA]+[iI]+[lL]$';
  private destroyed$ = new ReplaySubject<boolean>(1);

  // Constants for error messages
  private readonly LINKEDIN_ERROR_MESSAGE = 'Please add valid LinkedIn link';
  private readonly EMAIL_ERROR_MESSAGE = 'Please enter a valid email address';
  private readonly TEXT_AREA_MAX_CHAR_MESSAGE = (title: string) => `${title} must not exceed 2000 characters`;

  public constructor() {
    super();
    this.setDisplayName('BadgingQuestionsComponent');
  }

  public ngOnInit() {
    combineLatest([
      this.store.select(coreSelectors.isGuestUser),
      this.eventsService.getRegistration(this.eventsService.registrationId),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isGuest, registration]) => {
        if (registration.event?.socialRoomSetId === this.socialRoomSetId()) {
          this.registrationSignal.set(registration);
          this.registration = registration;
        }
        this.isGuestSignal.set(isGuest);
        void this.fetchBadgeQuestions();
        this.eventsService.registrationId = undefined;
      });
  }

  private async fetchBadgeQuestions(): Promise<void> {
    try {
      this.showLoaderSignal.set(true);
      this.showLoader = true;

      const questions = (await this.badgingService.getMyBadgeData(this.socialRoomSetId())) as unknown as Question[];
      this.badgeQuestionsSignal.set(questions);

      if (questions.length === 0) {
        this.joinSocial.emit(true);
        this.showLoaderSignal.set(false);
        this.showLoader = false;
        return;
      }

      const registration = this.registrationSignal();
      if (registration && registration.values && registration.values.length) {
        const registrationFields: string[] = registration.values.map((value) => value.name?.toLowerCase()) as string[];

        const updatedQuestions = questions.map((question) => {
          if (registrationFields.includes(question.title?.toLowerCase() ?? '') && question.title) {
            const index = registrationFields.indexOf(question.title?.toLowerCase() ?? '');
            const registrationQ: string = registration.values ? (registration.values[index]?.value ?? '') : '';
            // special treatment for multi-values
            if (question.inputType === QuestionInputType.MULTI_SELECT) {
              question.multiValues = registrationQ.split(',');
            }
            // set the input value to the registration value
            question.value = registrationQ;
          }
          return question;
        });

        this.badgeQuestionsSignal.set(updatedQuestions);
        // this.badgeQuestions = updatedQuestions;
      }

      // If the badge questions will be shown, then show the badge preview
      this.showBadgePreviewSignal.set(true);
      this.showBadgePreview = true;

      // Split badge questions into required and optional
      const requiredQuestions = questions.filter((question) => {
        return question.required;
      });
      this.requiredBadgeQuestionsSignal.set(requiredQuestions);
      this.requiredBadgeQuestions = requiredQuestions;

      const optionalQuestions = questions.filter((question) => {
        return !question.required;
      });
      this.optionalBadgeQuestionsSignal.set(optionalQuestions);
      this.optionalBadgeQuestions = optionalQuestions;

      const form = this.formBuilder.group(this.toFormGroup(questions));
      this.badgingFormSignal.set(form);
      this.badgingForm = form;

      this.handleFormValueChanges();
      form.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe({ next: () => this.handleFormValueChanges() });
    } catch (e) {
      this.error('Error when get badge data', e);
    }
  }

  // Helper function to create type checking functions
  protected isQuestionType(questionType: QuestionInputType, type: QuestionInputType): boolean {
    return questionType === type;
  }

  // Create functions to check if a question is of a specific type
  protected isTextArea(type: QuestionInputType): boolean {
    return this.isQuestionType(type, QuestionInputType.PARAGRAPH);
  }

  protected isTextInput(type: QuestionInputType): boolean {
    return this.isQuestionType(type, QuestionInputType.TEXT);
  }

  protected isSingleSelect(type: QuestionInputType): boolean {
    return this.isQuestionType(type, QuestionInputType.SINGLE_SELECT);
  }

  protected isMultiSelect(type: QuestionInputType): boolean {
    return this.isQuestionType(type, QuestionInputType.MULTI_SELECT);
  }

  protected isNumber(type: QuestionInputType): boolean {
    return this.isQuestionType(type, QuestionInputType.NUMBER);
  }

  // Compute if required badge questions exist
  protected readonly hasRequiredQuestions = computed(() => {
    const questions = this.requiredBadgeQuestionsSignal();
    return questions && questions.length > 0;
  });

  // Compute if optional badge questions exist
  protected readonly hasOptionalQuestions = computed(() => {
    const questions = this.optionalBadgeQuestionsSignal();
    return questions && questions.length > 0;
  });

  // Create form dynamically
  private toFormGroup(questions: Question[]): UntypedFormGroup {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group: any = {};
    questions.forEach((question) => {
      group[question.title] = question.required
        ? new UntypedFormControl(question.value ? question.value : '', [
            Validators.required,
            this.createLengthValidatorsForForm(question),
          ])
        : new UntypedFormControl(question.value ? question.value : '', [this.createLengthValidatorsForForm(question)]);

      if (question.inputType === QuestionInputType.MULTI_SELECT) {
        group[question.title].setValue(question.multiValues);
      }

      if (question.title.search(this.emailTitleRegex) === 0) {
        group[question.title].setValidators([
          Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,4}$'),
        ]);
      }

      if (question.title === 'LinkedIn') {
        group[question.title].setValidators([
          Validators.pattern('^(http(s)?://)?([w]+.)?linkedin.com/(pub|in|profile)/([-a-zA-Z0-9]+)/*$'),
        ]);
      }
    });
    this.showLoaderSignal.set(false);
    this.showLoader = false;
    return group;
  }

  // Create validation for value length in accord with input type
  private createLengthValidatorsForForm(question: Question) {
    return question.inputType === QuestionInputType.PARAGRAPH ? Validators.maxLength(2000) : Validators.maxLength(225);
  }

  public async submitBadgingForm(): Promise<void> {
    this.logUserAction('Submit Badging Form', 'clicked');

    const form = this.badgingFormSignal();
    form?.markAsTouched();
    this.badgingForm?.markAsTouched();

    if (form?.invalid) {
      this.markAllAsTouched();
      if (!this.inRoom()) {
        this.scrollToFirstError();
      }
      return;
    }

    this.showLoaderSignal.set(true);
    this.showLoader = true;

    const questions = this.badgeQuestionsSignal();
    const badgeDataValues: BadgeDataValue[] = [];

    questions.forEach((question: Question): void => {
      if (!question.title) {
        this.error('Error when updating badge data', 'No title for question');
        return;
      }
      const title = form?.controls[question.title]?.value;

      if (question.inputType === QuestionInputType.MULTI_SELECT) {
        question.multiValues = title;
      } else {
        question.value = title;
      }

      if (question.title.search(this.emailTitleRegex) === 0 && this.isGuestSignal()) {
        this.saveGuestEmail(question.value);
      }

      if (!question.value && !question.multiValues && question.required) {
        this.error('Error when updating badge data', 'No value for question', question);
        return;
      }

      if (title === 'firstName' && this.isGuestSignal()) {
        this.firstNameSignal.set(title.value);
      }

      if (title === 'lastName' && this.isGuestSignal()) {
        this.lastNameSignal.set(title.value);
      }

      badgeDataValues.push({
        propertyName: question.title ?? '',
        value: title ?? '',
        featured: question.featured ?? false,
      });
    });

    // Update badgeDataValue signal
    this.badgeDataValueSignal.set(badgeDataValues);

    const firstName = this.firstNameSignal();
    const lastName = this.lastNameSignal();
    if (this.isGuestSignal() && firstName && lastName) {
      this.updateGuestFirstNameLastName(firstName, lastName);
    }

    await this.badgingService
      .updateBadgeData(this.socialRoomSetId(), questions as unknown as BadgeData[])
      .then(() => {
        this.joinSocial.emit(true);
        if (this.inRoom()) {
          // TODO: The 'emit' function requires a mandatory void argument
          this.updatedSmartBadge.emit();
        }
      })
      .catch((e) => {
        this.error('Error when updating badge data', e);
      })
      .finally(() => {
        this.showLoaderSignal.set(false);
        this.showLoader = false;
      });
  }

  // Helper methods using signals
  protected getFormFieldWithSignal(name: string): AbstractControl | null | undefined {
    return this.badgingFormSignal()?.get(name);
  }

  // Helper method for getting question form value
  protected getQuestionFormValueWithSignal(title?: string) {
    if (!title) {
      return undefined;
    }
    return this.badgingFormSignal()?.get(title)?.value;
  }

  // Helper method for input error message using signals
  protected inputErrorMessageWithSignal(question?: BadgeQuestion): string {
    if (!question || !question.title) {
      return '';
    }

    const form = this.badgingFormSignal();
    if (!form) {
      return '';
    }

    if (form.get(question.title)?.errors?.required) {
      return `${question.title} is required`;
    }

    if (question.title === 'LinkedIn') {
      return `Please add valid LinkedIn link`;
    }

    if (question.title.search(this.emailTitleRegex) === 0) {
      return `Please enter a valid email address`;
    }

    if (question.inputType === QuestionInputType.PARAGRAPH) {
      return `${question.title} must not exceed 2000 characters`;
    }

    return '';
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public shouldShowHeader(badgeQuestions: BadgeQuestion[]): boolean {
    return badgeQuestions && badgeQuestions.length > 0 ? true : false;
  }

  public getFormField(name: string): AbstractControl | null | undefined {
    return this.badgingFormSignal()?.get(name);
  }

  public getQuestionFormValue(title?: string) {
    if (!title) {
      return;
    }
    return this.badgingFormSignal()?.get(title)?.value;
  }

  public inputErrorMessage(badgeQuestion: BadgeQuestion): string {
    if (!badgeQuestion || !badgeQuestion.title) {
      return '';
    }
    if (badgeQuestion.title === 'LinkedIn') {
      return this.LINKEDIN_ERROR_MESSAGE;
    }
    if (badgeQuestion.title.match(this.emailTitleRegex)) {
      return this.EMAIL_ERROR_MESSAGE;
    }
    if (badgeQuestion.inputType === QuestionInputType.PARAGRAPH) {
      return this.TEXT_AREA_MAX_CHAR_MESSAGE(badgeQuestion.title);
    }
    return '';
  }

  private handleFormValueChanges(): void {
    const questions = this.badgeQuestionsSignal();
    const form = this.badgingFormSignal();

    if (!form) {
      return;
    }

    const badgeDataValues: BadgeDataValue[] = [];

    questions.forEach((question) => {
      if (question.title) {
        const questionValue = form.controls[question.title]?.value;
        badgeDataValues.push({
          propertyName: question.title ?? '',
          value: questionValue ?? '',
          featured: question.featured ?? false,
        });
      }
    });

    this.badgeDataValueSignal.set(badgeDataValues);
    this.badgeDataValue = badgeDataValues;

    this.badgingService.setPreviewBadgeData(badgeDataValues);
  }

  private markAllAsTouched() {
    const form = this.badgingFormSignal();
    if (!form) {
      return;
    }

    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  private scrollToFirstError() {
    const form = this.badgingFormSignal();
    const controls = form?.controls;

    if (!controls) {
      return;
    }

    for (const name in controls) {
      if (controls[name].invalid) {
        const invalidControl = document.getElementById(`badge-${name}`);
        if (invalidControl) {
          invalidControl.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      }
    }
  }

  private updateGuestFirstNameLastName(firstName: string, lastName: string): void {
    const user = this.userSignal();
    const updatedUser = { ...user, firstName: firstName, lastName: lastName };

    if (firstName.trim() !== user?.firstName && lastName.trim() !== user?.lastName) {
      userController.updateUser(updatedUser).catch((err: unknown) => {
        this.error('Unable to update guest name', err);
      });
    }
  }

  private saveGuestEmail(email?: string) {
    if (!email) {
      return;
    }
    sessionStorage.setItem(GUEST_EMAIL_KEY, email);
  }

  protected scrollIntoFieldView(id: string) {
    if (this.inRoom()) {
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
